import Editor from "react-simple-code-editor";
import { highlight, languages } from "prismjs";
import "prismjs/components/prism-sql";
import "prismjs/themes/prism-tomorrow.css";
import { ChevronDown, ChevronRight, Plus, Trash2, X } from "lucide-react";
import { Badge, Button, Card, Field, Input, Select, Textarea, type BadgeTone } from "@/components/ui";
import { cn } from "@/lib/utils";
import { DIFFICULTY_LABELS, type DatasetQuestion } from "./types";

const DIFFICULTY_TONES: Record<string, BadgeTone> = { easy: "success", medium: "warning", hard: "danger" };

export function DifficultyBadge({ difficulty }: { difficulty: string }) {
  return <Badge tone={DIFFICULTY_TONES[difficulty] ?? "neutral"}>{DIFFICULTY_LABELS[difficulty] ?? difficulty}</Badge>;
}

export function QuestionEditor({
  question: q,
  index,
  expanded,
  onToggle,
  onChange,
  onRemove,
}: {
  question: DatasetQuestion;
  index: number;
  expanded: boolean;
  onToggle: () => void;
  onChange: (field: keyof DatasetQuestion, value: any) => void;
  onRemove: () => void;
}) {
  const fieldId = (name: string) => `q-${index}-${name}`;

  return (
    <Card className={cn("overflow-hidden transition-colors", expanded && "border-sl-charcoal/30")}>
      <div className="flex items-start gap-3 px-4 py-3">
        <button
          onClick={onToggle}
          className="flex min-w-0 flex-1 items-start gap-3 text-left"
          aria-expanded={expanded}
          title={expanded ? "Inklappen" : "Uitklappen"}
        >
          <span className="mt-0.5 shrink-0 text-muted-foreground">
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-sm font-semibold">{q.id}</span>
              <DifficultyBadge difficulty={q.difficulty} />
              {q.domain && <Badge>{q.domain}</Badge>}
            </span>
            {!expanded && (
              <span className={cn("mt-1 line-clamp-2 block text-sm", q.question ? "text-foreground/80" : "italic text-muted-foreground")}>
                {q.question || "Nog geen vraag ingevuld"}
              </span>
            )}
          </span>
        </button>
        <div className="flex shrink-0 gap-1">
          <Button variant="ghost" size="sm" onClick={onToggle}>
            {expanded ? "Inklappen" : "Bewerken"}
          </Button>
          <Button variant="ghost" size="icon" onClick={onRemove} title="Vraag verwijderen" aria-label="Vraag verwijderen" className="text-destructive hover:bg-[#FDECEC]">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="grid gap-4 border-t border-border bg-sl-grey-50 px-4 py-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="ID" htmlFor={fieldId("id")}>
              <Input id={fieldId("id")} value={q.id} onChange={(e) => onChange("id", e.target.value)} className="font-mono" />
            </Field>
            <Field label="Domein" htmlFor={fieldId("domain")}>
              <Input id={fieldId("domain")} value={q.domain || ""} onChange={(e) => onChange("domain", e.target.value)} />
            </Field>
            <Field label="Moeilijkheid" htmlFor={fieldId("difficulty")}>
              <Select id={fieldId("difficulty")} value={q.difficulty} onChange={(e) => onChange("difficulty", e.target.value)}>
                <option value="easy">{DIFFICULTY_LABELS.easy}</option>
                <option value="medium">{DIFFICULTY_LABELS.medium}</option>
                <option value="hard">{DIFFICULTY_LABELS.hard}</option>
              </Select>
            </Field>
          </div>

          <Field label="Vraag" htmlFor={fieldId("question")}>
            <Textarea id={fieldId("question")} value={q.question} onChange={(e) => onChange("question", e.target.value)} rows={2} />
          </Field>

          {q.question_en !== undefined && (
            <Field label="Vraag (Engels)" htmlFor={fieldId("question_en")}>
              <Textarea id={fieldId("question_en")} value={q.question_en || ""} onChange={(e) => onChange("question_en", e.target.value)} rows={2} />
            </Field>
          )}

          <Field label="Verwachte tabellen">
            <div className="space-y-2">
              {(q.expected_tables || []).map((table, tableIdx) => (
                <div key={tableIdx} className="flex gap-2">
                  <Input
                    value={table}
                    onChange={(e) => {
                      const newTables = [...(q.expected_tables || [])];
                      newTables[tableIdx] = e.target.value;
                      onChange("expected_tables", newTables);
                    }}
                    className="font-mono"
                    placeholder="Tabelnaam"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-10 w-10 shrink-0 text-destructive"
                    title="Tabel verwijderen"
                    aria-label="Tabel verwijderen"
                    onClick={() => {
                      const newTables = [...(q.expected_tables || [])];
                      newTables.splice(tableIdx, 1);
                      onChange("expected_tables", newTables);
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => onChange("expected_tables", [...(q.expected_tables || []), ""])}>
                <Plus className="h-4 w-4" />
                Tabel toevoegen
              </Button>
            </div>
          </Field>

          <Field label="Referentie-SQL">
            <div className="overflow-hidden rounded-md border border-sl-charcoal/40 bg-sl-charcoal">
              <Editor
                value={q.reference_sql}
                onValueChange={(code) => onChange("reference_sql", code)}
                highlight={(code) => highlight(code, languages.sql, "sql")}
                padding={12}
                tabSize={2}
                insertSpaces={true}
                style={{
                  fontFamily: 'ui-monospace, SFMono-Regular, "JetBrains Mono", Menlo, Consolas, monospace',
                  fontSize: 13,
                  lineHeight: 1.6,
                  color: "#F3F5F8",
                  backgroundColor: "#212427",
                  minHeight: "300px",
                }}
                textareaClassName="focus:outline-hidden"
              />
            </div>
          </Field>

          <Field label="Verwacht antwoord (leesbaar)" htmlFor={fieldId("hra")}>
            <Textarea
              id={fieldId("hra")}
              value={q.human_readable_answer || ""}
              onChange={(e) => onChange("human_readable_answer", e.target.value)}
              rows={4}
              placeholder="Het verwachte antwoord in gewone taal"
            />
          </Field>

          <Field label="Toelichting" htmlFor={fieldId("explanation")}>
            <Textarea id={fieldId("explanation")} value={q.explanation || ""} onChange={(e) => onChange("explanation", e.target.value)} rows={3} />
          </Field>
        </div>
      )}
    </Card>
  );
}
